// [목적] 알림 도메인 훅 세트 — 목록(무한스크롤)/미읽 카운트/읽음/모두읽음 mutation
// [이유] fe-be-gap-tier3 T8(ADR-TR3-2/3/6) — NotificationsPage + Sidebar 뱃지 실데이터 연결.
//         useNotifications: 탭별 useInfiniteQuery(nextCursor 키셋). useUnreadCount: unreadCount 노출.
//         useMarkNotificationRead / useMarkAllRead: mutation + 캐시 invalidate.
// [사이드 이팩트] useUnreadCount 는 staleTime 없음 + refetchOnWindowFocus: true(ADR-T3-6).
//                 주기 폴링 없음(ADR-T3-6). 리스트 invalidate 는 tab 무관 전체(notification.list 루트 prefix).
// [수정 시 고려사항] useInfiniteQuery pageParam 은 cursor string | undefined.
//                   markRead/markAllRead 성공 시 notification.unread() + notification.list(모든탭) invalidate.

import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type NotificationTab } from '@gangwoncanvas/types';

import * as notificationClient from '../api/notification.client.js';
import { queryKeys } from '../api/queryKeys.js';

// ─── 목록 훅 ──────────────────────────────────────────────────────────────────

/**
 * [목적] 탭별 알림 목록 — useInfiniteQuery (nextCursor 키셋 페이지네이션)
 * [이유] fe-be-gap-tier3 T9 — NotificationsPage 에서 탭 전환 시 독립 쿼리 엔트리.
 *         initialPageParam: undefined (첫 페이지는 cursor 없이 요청).
 *         getNextPageParam: 마지막 페이지의 nextCursor 반환 (null 이면 로드 없음).
 * [사이드 이팩트] tab 변경 시 query key 변경으로 자동 재요청.
 *                 2026-09-20 notification-bell: options.enabled 를 받아 "패널이 열렸을 때만 조회"를
 *                 지원한다 — TopBar 벨은 전 페이지에 상주하므로 기본 활성이면 모든 페이지 진입마다
 *                 알림 목록을 불필요하게 받아온다(미읽 카운트는 useUnreadCount 가 따로 담당).
 * [수정 시 고려사항] options 미전달 시 기존대로 항상 활성(NotificationsPage 호환).
 *                   비로그인 시 BE 401 → ApiError throw.
 */
export function useNotifications(tab: NotificationTab = 'all', options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: queryKeys.notification.list(tab),
    enabled: options?.enabled ?? true,
    queryFn: ({ pageParam }) =>
      notificationClient.getNotifications({
        tab,
        cursor: pageParam as string | undefined,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 1000 * 30, // 30초 — 목록은 읽음 처리 후 invalidate 로 갱신
  });
}

// ─── 미읽 카운트 훅 ───────────────────────────────────────────────────────────

/**
 * [목적] 본인 미읽 알림 전체 카운트 — Sidebar 뱃지 + NotificationsPage 헤더
 * [이유] fe-be-gap-tier3 T8/T10(ADR-T3-6) — 진입 시 조회 + window focus refetch.
 *         주기 폴링 없음, 실시간 push 없음(ADR-T3-6 확정).
 *         tab=all / limit=1 로 최소 요청 후 unreadCount 필드만 사용.
 * [사이드 이팩트] refetchOnWindowFocus: true — 탭 이동 후 복귀 시 자동 갱신.
 *                 staleTime: 0 — 항상 fresh 조회(뱃지 정확도 중시).
 * [수정 시 고려사항] throwOnError: false — Sidebar 뱃지는 실패 시 숨김 처리.
 */
export function useUnreadCount(): number {
  const { data } = useQuery({
    queryKey: queryKeys.notification.unread(),
    queryFn: () => notificationClient.getNotifications({ tab: 'all', limit: 1 }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    throwOnError: false,
  });
  return data?.unreadCount ?? 0;
}

// ─── 읽음 mutation 훅 ────────────────────────────────────────────────────────

/**
 * [목적] 단건 알림 읽음 처리 mutation
 * [이유] fe-be-gap-tier3 T9 — 알림 항목 클릭 시 호출. 성공 후 목록 + 미읽 카운트 invalidate.
 * [사이드 이팩트] 읽음 처리 성공 시 notification.list(전체) + notification.unread() 동시 invalidate.
 * [수정 시 고려사항] idempotent — 이미 읽음인 알림을 재호출해도 BE 는 성공 반환.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationClient.markRead(id),
    onSuccess: () => {
      // 탭 무관 전체 목록 invalidate (prefix 매칭)
      void queryClient.invalidateQueries({ queryKey: ['notification', 'list'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notification.unread() });
    },
  });
}

/**
 * [목적] 본인 미읽 알림 전체 읽음 처리 mutation
 * [이유] fe-be-gap-tier3 T9 — "모두 읽음" 버튼 클릭 시 호출. 성공 후 invalidate.
 * [사이드 이팩트] markAllRead 성공 시 unreadCount=0 으로 뱃지 소멸 + 목록 재조회.
 * [수정 시 고려사항] BE affected 건수를 반환하지만 FE 에서는 invalidate 로 최신 상태 유지.
 */
export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationClient.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notification', 'list'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notification.unread() });
    },
  });
}
